import React, { useEffect, useMemo, useRef, useState } from "react";
import { tgIcon } from "../../../images";
import PsPricesDealsPage from "../../pages/pspricesDealsPage";
import PsPickedGame from "../../components/psDeals/PsPickedGame";
import {
  PS_CARD_REGIONS,
  bestPsPlan,
  psCatalogs,
  usePsCards,
} from "../../components/psDeals/psCards";
import { rubAtCardRate } from "../../utils/pickCards";
import FavoritesStrip from "../../favorites/FavoritesStrip";
import { favoriteFromPsPick } from "../../favorites/favoriteKey";

const REGIONS = [
  { value: "us", label: "США" },
  { value: "pl", label: "Польша" },
  { value: "in", label: "Индия" },
];

const pillClass =
  "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 font-medium shadow-sm transition";
const outlinePill = `${pillClass} border-gray-200 bg-white text-gray-700 hover:border-indigo-300 hover:text-indigo-700 dark:border-gray-600 dark:bg-[#2a2a2a] dark:text-gray-200`;

function getBuyOnSiteUrl(region) {
  if (region === "us") {
    return "https://trendy-eng-shop.ru/store/playstation";
  }
  return "https://trendy-eng-shop.ru/playstation/shop";
}

function RegionPills({ region, onChange }) {
  return (
    <div className="mt-4 flex justify-center gap-2">
      {REGIONS.map((item) => (
        <button
          key={item.value}
          type="button"
          onClick={() => onChange(item.value)}
          aria-pressed={region === item.value}
          className={
            region === item.value
              ? `${pillClass} border-indigo-600 bg-indigo-600 text-white`
              : outlinePill
          }
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}

function BuyLinks({ region }) {
  return (
    <div className="mx-auto mt-4 flex max-w-3xl flex-wrap items-center justify-center gap-2 px-1 text-sm">
      <span className="basis-full text-center text-gray-500 dark:text-gray-400 sm:mr-1 sm:basis-auto">
        Карты пополнения PSN:
      </span>
      <a
        href={getBuyOnSiteUrl(region)}
        className={`${pillClass} border-indigo-600 bg-indigo-600 text-white hover:bg-indigo-500`}
        target="_blank"
        rel="noreferrer"
      >
        На сайте
      </a>
      <a
        href="https://t.me/trendy_psn_bot?start=ref-001"
        className={outlinePill}
        target="_blank"
        rel="noreferrer"
        onClick={() => window.ym?.(93997310, "reachGoal", "tgClickPlayStation")}
      >
        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#2AABEE]">
          <img src={tgIcon} alt="" className="h-3 w-3" />
        </span>
        Telegram
      </a>
    </div>
  );
}

/** Игра из витрины → { game, region, currency, label, rub } или null, если карт не подобрать. */
function buildDealPick(deal, region, catalogs) {
  const config = PS_CARD_REGIONS[region];
  if (!config || !(deal.amount > 0) || deal.currency !== config.currency) return null;
  const best = bestPsPlan(deal.amount, catalogs);
  if (!best) return null;
  return {
    game: {
      id: deal.id,
      title: deal.name,
      image: deal.cover,
      price: deal.amount,
      original: deal.originalAmount,
      percent: deal.discountPercent,
      endsAt: deal.discountEndDate,
      storeUrl: deal.storeUrl,
    },
    region,
    currency: config.currency,
    label: REGIONS.find((item) => item.value === region)?.label,
    rub: rubAtCardRate(deal.amount, best.catalog.denominations) ?? best.plan.rub,
  };
}

function DealBuy({ pick, onBuy }) {
  return (
    <div className="flex items-center justify-between gap-1 pb-1">
      <span className="text-xs font-medium text-indigo-700 dark:text-indigo-300">
        ≈ {Math.round(pick.rub).toLocaleString("ru-RU")} ₽
      </span>
      <button
        type="button"
        onClick={onBuy}
        className="shrink-0 whitespace-nowrap rounded bg-indigo-600 px-1.5 py-0.5 text-[11px] font-semibold leading-tight text-white hover:bg-indigo-500"
      >
        Купить код
      </button>
    </div>
  );
}

function PlayStationDiscount() {
  const cards = usePsCards();
  const { cardsFor } = cards;
  const [dealPick, setDealPick] = useState(null);
  const panelRef = useRef(null);

  const catalogsFor = useMemo(() => {
    const cache = new Map();
    return (region) => {
      if (!cache.has(region)) cache.set(region, psCatalogs(region, cardsFor));
      return cache.get(region);
    };
  }, [cardsFor]);

  const pickFor = useMemo(() => {
    const cache = new Map();
    return (deal, region) => {
      const key = `${region}:${deal.id}`;
      if (!cache.has(key)) cache.set(key, buildDealPick(deal, region, catalogsFor(region)));
      return cache.get(key);
    };
  }, [catalogsFor]);

  useEffect(() => {
    if (dealPick) panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [dealPick]);

  const buyAction = (deal, region) => {
    const pick = pickFor(deal, region);
    return pick ? () => setDealPick({ ...pick }) : null;
  };

  const openFavorite = (item, deal, region) => {
    const pick = deal ? pickFor(deal, region) : null;
    if (pick) {
      setDealPick({ ...pick });
    } else if (item.url) {
      window.open(item.url, "_blank", "noopener");
    }
  };

  return (
    <PsPricesDealsPage
      title="Скидки PlayStation"
      platforms="PS5"
      platformQuery="PS5"
      regions={REGIONS}
      ymPath="/playstation-discount"
      favoritePlatform="playstation"
      getDealImageAction={buyAction}
      renderDealAction={(deal, region) => {
        const pick = pickFor(deal, region);
        return pick ? <DealBuy pick={pick} onBuy={() => setDealPick({ ...pick })} /> : null;
      }}
      renderTop={({ region, onRegionChange, deals }) => (
        <>
          <p className="-mt-1 text-center text-sm text-gray-500 dark:text-gray-400 md:-mt-3">
            {PS_CARD_REGIONS[region]
              ? "Цены PlayStation Store со скидками и в рублях — по курсу наших карт PSN"
              : "Цены PlayStation Store со скидками. Карт PSN этого региона на сайте пока нет"}
          </p>
          <RegionPills region={region} onChange={onRegionChange} />
          {dealPick && dealPick.region === region ? (
            <PsPickedGame
              ref={panelRef}
              pick={dealPick}
              catalogs={catalogsFor(region)}
              cardsError={cards.error}
              onRefreshCards={cards.refresh}
              onClose={() => setDealPick(null)}
              favorite={favoriteFromPsPick(dealPick)}
            />
          ) : null}
          <BuyLinks region={region} />
          <FavoritesStrip
            platform="playstation"
            region={region}
            deals={deals}
            onOpen={(item, deal) => openFavorite(item, deal, region)}
          />
          <h2 className="mx-4 mt-5 text-center text-lg font-semibold dark:text-white md:mt-6">
            Популярные скидки
          </h2>
        </>
      )}
    />
  );
}

export default PlayStationDiscount;
